import { useCallback, useMemo } from 'react';
import { useRouter } from '@tanstack/react-router';

import { CardProps } from '@ui/components/Card';
import { useMediaQuery } from '@providers/MediaQueryProvider';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import {
  IEventsQueryCustomization,
  useEventsWithPaging,
} from '@features/club/hooks/useEventsWithPaging';
import { ClubEventList } from '@features/club/ui/ClubEventList';
import { ClubNewsWidget } from '@features/club/ui/ClubNews/ClubNewsWidget';
import { useUserSocket } from '@features/shared/api';
import { components } from '@features/shared/api/club/clients';

import { ITab, TABS } from './constants';

export type Props = {
  id?: string;
  className?: string;
  clubId: string;
  tab: string;
  subTab?: string;
  hideNews?: boolean;
  eventsTheme?: CardProps['theme'];
  customTitle?: string;
  customTabs?: readonly ITab[];
  customSubTabs?: ITab[];
  customFilter?: (
    events: components['schemas']['TournamentShortDto'][],
    type?: string,
    subtype?: string,
  ) => components['schemas']['TournamentShortDto'][];
  customQuery?: (type?: string, subtype?: string) => IEventsQueryCustomization | undefined;
  customEventEntranceText?: string;
  onTabChange: (tab: string, subTab?: string) => void;
};

export function ClubEventListWidget({
  id,
  className,
  clubId,
  tab,
  subTab,
  hideNews,
  eventsTheme,
  customTitle,
  customTabs,
  customSubTabs,
  customFilter,
  customQuery,
  customEventEntranceText,
  onTabChange,
}: Props) {
  const router = useRouter();
  const getTournamentHref = useCallback(
    (tournamentId: string) =>
      router.buildLocation({
        to: '/club/$clubId/tournament/$tournamentId',
        params: { clubId, tournamentId },
        from: '/',
      }).href,
    [router, clubId],
  );

  const { isManager } = useClubRoles(clubId);

  const { events, refetch, isPending, fetchNextPage, query, setQuery } = useEventsWithPaging({
    clubId,
    type: tab,
    subType: subTab,
    customFilter,
    customQuery,
  });

  useUserSocket((e) => {
    if (tab === 'finished' || tab === 'drafts') return;
    switch (e.eventType) {
      case 'TournamentPublished':
      case 'TournamentUpdated':
        refetch();
        break;
    }
  });

  let news = undefined;
  if (!hideNews) {
    news = (className?: string) => <ClubNewsWidget className={className} clubId={clubId} />;
  }

  const isMobile = useMediaQuery() === 'mobile';
  const tabs = useMemo(
    () =>
      (customTabs ?? TABS).reduce((sink, t) => {
        switch (t.value) {
          case 'drafts':
            if (isManager && !isMobile) {
              sink.push(t);
            }
            break;
          default:
            sink.push(t);
        }

        return sink;
      }, [] as ITab[]),
    [customTabs, isManager, isMobile],
  );

  return (
    <ClubEventList
      id={id}
      title={customTitle ?? 'События'}
      className={className}
      tabs={tabs}
      tab={tab}
      subtabs={customSubTabs}
      subTab={subTab}
      loading={isPending}
      // TODO fix typings, separate manager and user types, separate list and card types
      events={events}
      eventsTheme={eventsTheme}
      customEventEntranceText={customEventEntranceText}
      onTab={onTabChange}
      getTournamentHref={getTournamentHref}
      isManager={isManager}
      news={news}
      query={query}
      onQueryChange={setQuery}
      onLoadMore={fetchNextPage}
    />
  );
}
